import { CustomFieldDefinition, Location } from '@nx-smartmonkey/shared/domain';

import { SubscriptionCredentials } from '../domain/credentials/SubscriptionCredentials';
import { GeoFence } from '../domain/geoFence/GeoFence';
import { FlatProject } from '../domain/project/FlatProject';
import { Project } from '../domain/project/Project';
import { ProjectCustomerEmailTypes } from '../domain/project/ProjectDTO';
import { ProjectUser } from '../domain/project/ProjectUser';
import { ProjectUserRoles } from '../domain/project/ProjectUserRoles';
import { Vehicle } from '../domain/vehicle/Vehicle';
import { ProjectCustomerEmailMapper } from '../mappers/project/ProjectCustomerEmailMapper';
import { ProjectCustomerSMSMapper } from '../mappers/project/ProjectCustomerSMSMapper';
import { projectRepository } from '../repositories/project';
import { ProjectRepository } from '../repositories/project/ProjectRepository';
import eventsPanel from '../services/events';

export class ProjectService {
  constructor(private projectRepository: ProjectRepository) {}

  async create({
    label,
    description,
    location,
    traffic,
    vehicles,
    users,
    timeZone,
  }: {
    label?: string;
    description?: string;
    location?: Location | null;
    traffic?: boolean;
    vehicles: Vehicle[];
    users: string[];
    timeZone: string;
  }): Promise<Project> {
    const project = await this.projectRepository.create({
      label,
      description,
      location,
      vehicles,
      traffic,
      users,
      timeZone,
    });
    return project;
  }

  async delete({ project }: { project: Project }): Promise<Project> {
    await this.projectRepository.delete({ project });
    return project;
  }

  async fetch({ projectId }: { projectId: string }): Promise<Project> {
    const project = await this.projectRepository.fetch({ projectId });
    return project;
  }

  async fetchAll(): Promise<Project[]> {
    const projects = await this.projectRepository.fetchAll();
    return projects;
  }

  async fetchAllFlat(): Promise<FlatProject[]> {
    const projects = await this.projectRepository.fetchAllFlat();
    return projects;
  }

  async save({
    project,
    subscriptionCredentials,
  }: {
    project: Project;
    subscriptionCredentials: SubscriptionCredentials;
  }): Promise<Project> {
    return this.projectRepository.save(project, subscriptionCredentials);
  }

  async updateCommunicationEmails(project: Project): Promise<Project> {
    const updatedProject = await this.projectRepository.updateCommunicationEmails(project);

    eventsPanel.projectCommunication({
      action: `edit_email`,
      data: ProjectCustomerEmailMapper.toRepository(project.customer_email),
    });

    return updatedProject;
  }

  async updateCommunicationSMS(project: Project): Promise<Project> {
    const updatedProject = await this.projectRepository.updateCommunicationSMS(project);

    eventsPanel.projectCommunication({
      action: `edit_sms`,
      data: ProjectCustomerSMSMapper.toRepository(project.customer_sms),
    });

    return updatedProject;
  }

  async sendTestCommunicationEmail({
    project,
    type,
    email,
    from,
    subject,
    body,
  }: {
    project: Project;
    type: ProjectCustomerEmailTypes;
    email: string;
    from?: string;
    subject?: string;
    body?: string;
  }): Promise<void> {
    await this.projectRepository.sendTestCommunicationEmail({
      projectId: project.id,
      payload: {
        type,
        email,
        from,
        subject,
        body,
      },
    });
  }

  async fetchAvatar({ project }: { project: Project }): Promise<Project> {
    return this.projectRepository.fetchAvatar({ project });
  }

  async createAvatar({
    project,
    fileName,
    mimeType,
    file,
  }: {
    project: Project;
    fileName: string;
    mimeType: string;
    file: Blob;
  }): Promise<Project> {
    const updatedProject = await this.projectRepository.createAvatar({
      project,
      fileName,
      mimeType,
      file,
    });
    return updatedProject;
  }

  async deleteAvatar({ project }: { project: Project }): Promise<Project> {
    const updatedProject = await this.projectRepository.deleteAvatar({ project });
    return updatedProject;
  }

  async createStyleImage({
    project,
    fileName,
    mimeType,
    imageType,
    file,
    domain,
  }: {
    project: Project;
    fileName: string;
    mimeType: string;
    imageType: `logo` | `banner` | `favicon`;
    file: Blob;
    domain: `customers` | `supervisors`;
  }): Promise<void> {
    return this.projectRepository.createStyleImage({
      project,
      fileName,
      mimeType,
      imageType,
      file,
      domain,
    });
  }

  async deleteStyleImage({
    project,
    imageType,
    domain,
  }: {
    project: Project;
    imageType: `logo` | `banner` | `favicon`;
    domain: `customers` | `supervisors`;
  }): Promise<void> {
    return this.projectRepository.deleteStyleImage({ project, imageType, domain });
  }

  async retrieveUsers({ project }: { project: Project }): Promise<ProjectUser[]> {
    return this.projectRepository.retrieveUsers({ project });
  }

  async createUser({
    userId,
    role,
    project,
  }: {
    userId: string;
    role: ProjectUserRoles;
    project: Project;
  }): Promise<Project> {
    return this.projectRepository.createUser({
      userId,
      role,
      project,
    });
  }

  async deleteUser({ projectUser, project }: { projectUser: ProjectUser; project: Project }): Promise<Project> {
    return this.projectRepository.deleteUser({
      projectUser,
      project,
    });
  }

  async editUserRole({ projectUser, project }: { projectUser: ProjectUser; project: Project }): Promise<Project> {
    return this.projectRepository.editUser({ projectUser, project });
  }

  async createCustomField({
    customFieldDefinition,
  }: {
    customFieldDefinition: CustomFieldDefinition;
  }): Promise<Project> {
    return this.projectRepository.createCustomField({ customFieldDefinition });
  }

  async deleteCustomField({ customField }: { customField: CustomFieldDefinition }): Promise<Project> {
    return this.projectRepository.deleteCustomField({ customField });
  }

  async updateCustomField({
    label,
    description,
    enabled,
    customField,
  }: {
    label?: string;
    description?: string;
    enabled?: boolean;
    customField: CustomFieldDefinition;
  }): Promise<Project> {
    return this.projectRepository.updateCustomField({
      label,
      description,
      enabled,
      customField,
    });
  }

  async fetchProjectGeoFences({ projectId }: { projectId: String }): Promise<GeoFence[]> {
    return this.projectRepository.fetchProjectGeoFences({ projectId });
  }

  async fetchGeoFence({ geoFenceId }: { geoFenceId: string }): Promise<GeoFence> {
    return this.projectRepository.fetchGeoFence({ geoFenceId });
  }

  async createGeoFence({
    project,
    geoFence,
  }: {
    project: Project;
    geoFence: GeoFence;
  }): Promise<{ project: Project; newGeoFence: GeoFence }> {
    const newGeoFence = await this.projectRepository.createGeoFence({ project, geoFence });
    return { project, newGeoFence };
  }

  async updateGeoFence({ geoFence }: { geoFence: GeoFence }): Promise<GeoFence> {
    await this.projectRepository.updateGeoFence({ geoFence });
    return geoFence;
  }

  async updateGeoFences({ geoFences }: { geoFences: GeoFence[] }): Promise<GeoFence[]> {
    await this.projectRepository.updateGeoFences({ geoFences });
    return geoFences;
  }

  async deleteGeoFence({ project, geoFence }: { project: Project; geoFence: GeoFence }): Promise<Project> {
    await this.projectRepository.deleteGeoFence({ geoFence });
    return project;
  }

  async deleteGeoFences({ project, geoFences }: { project: Project; geoFences: GeoFence[] }): Promise<Project> {
    await this.projectRepository.deleteGeoFences({ geoFences });
    return project;
  }
  async updateProjectTraffic({ projectId, traffic }: { projectId: string; traffic: boolean }): Promise<void> {
    await this.projectRepository.updateProjectTraffic({
      projectId,
      traffic,
    });
  }
}

export const projectService = new ProjectService(projectRepository);
